import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { SiZoho } from 'react-icons/si';
import PageLayout from '../../../components/layouts/PageLayout';
import CustomNavbar from '../../../components/common/CustomNavbar';
import { ToastKind, ZohoTabKey } from '../../../types/integrations';
import { useZoho } from '../../../util/hooks/useZoho';
import ZohoHeader from './ZohoHeader';
import ZohoTabs from './ZohoTabs';
import ConnectTab from './ConnectTab';
import FlowsTab from './FlowsTab';
import ActivityTab from '../../../components/common/ActivityTab';
import SettingsTab from '../../../components/common/SettingsTab';


interface ToastState {
  msg: string;
  kind: ToastKind;
}

export function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<number | null>(null);

  const show = useCallback((msg: string, kind: ToastKind = 'good') => {
    if (timer.current) window.clearTimeout(timer.current);
    setToast({ msg, kind });
    timer.current = window.setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );

  return { toast, show };
}

const Zoho: FC = () => {
  const { info, loading, error, reload, disconnect } = useZoho();
  const [tab, setTab] = useState<ZohoTabKey | null>(null);
  const { toast: toastState, show: showToast } = useToast();

  // Set default tab once info loads
  useEffect(() => {
    if (!info) return;
    setTab((t) => t ?? (info.status === 'CONNECTED' ? 'flows' : 'connect'));
  }, [info]);

  // Handle OAuth callback: /admin/integrations/zoho?zoho=ok | ?zoho=fail&reason=…
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const zoho = q.get('zoho');
    if (!zoho) return;

    if (zoho === 'ok') showToast('Zoho CRM is connected');
    else showToast(q.get('reason') ?? 'Zoho CRM was not connected', 'bad');

    setTab('connect');
    window.history.replaceState(null, '', window.location.pathname);
    void reload();
  }, [reload, showToast]);

  const connected = info?.status === 'CONNECTED';

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        'Disconnect Zoho CRM? Scheduled messages will be cancelled.'
      )
    )
      return;
    await disconnect();
    showToast('Zoho CRM disconnected');
  };

  return (
    <PageLayout
      customNavbar={
        <CustomNavbar>
          <div className="flex w-max items-center gap-3">
            <SiZoho className="text-2xl text-st-red" />
            <span className="text-lg font-bold uppercase">Zoho CRM</span>
          </div>
        </CustomNavbar>
      }
    >
      <section className="p-4 sm:p-6 bg-white rounded-lg">
        <div className=" mx-auto">
          <ZohoHeader
            info={info}
            onDisconnect={handleDisconnect}
          />

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {info?.lastError && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              {info.lastError}
            </div>
          )}

          {loading && !info && (
            <div className="h-48 rounded-xl bg-slate-200 animate-pulse" />
          )}

          {info && tab && (
            <>
              <ZohoTabs active={tab} onChange={setTab} connected={connected} />

              {tab === 'connect' && (
                <ConnectTab info={info} onDone={reload} toast={showToast} />
              )}
              {tab === 'flows' && (
                <FlowsTab connected={connected} toast={showToast} />
              )}
              {tab === 'activity' && <ActivityTab />}
              {tab === 'settings' && (
                <SettingsTab info={info} onSaved={reload} toast={showToast} />
              )}
            </>
          )}
        </div>
      </section>

      {toastState && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg ${
            toastState.kind === 'bad'
              ? 'bg-red-600 text-white'
              : 'bg-slate-900 text-white'
          }`}
          role="status"
        >
          {toastState.msg}
        </div>
      )}
    </PageLayout>
  );
};

export default Zoho;